/**
 * placeOfSupply.ts — decide whether a supply is inter-state.
 *
 * Getting this wrong sends tax to the wrong government: charge CGST+SGST on an
 * inter-state supply and the buyer's input credit is at risk; charge IGST on an
 * intra-state one and the state gets nothing. It is also a portal rejection —
 * GSTR-1 will not accept a B2B row whose place of supply differs from the
 * supplier's state while carrying CGST/SGST.
 *
 * Until now nothing derived this. Outward supply relied on a cashier remembering
 * to tick a checkbox; inward supply didn't consider the supplier's state at all,
 * so the tax head depended on which purchase screen happened to be used.
 *
 * The GSTIN's first two digits ARE the state code, so a registered counterparty
 * needs no lookup. Fall back to the address state, and finally to "unknown" —
 * never to a guess.
 */

import { isValidGstin, stateCodeFromGstin, stateCodeFromName } from '@/reports/gst/gstStates';

export interface CounterpartyLocation {
  /** Customer's or supplier's GSTIN, if registered. */
  gstin?: string | null;
  /** Their address state, used when there is no valid GSTIN. */
  state?: string | null;
}

/**
 * The counterparty's two-digit state code, or null when it cannot be established.
 * A valid GSTIN wins: it is the state the party is registered in, which is what
 * the place-of-supply rules turn on.
 */
export function counterpartyStateCode(party: CounterpartyLocation | null | undefined): string | null {
  if (!party) return null;
  if (isValidGstin(party.gstin)) {
    const fromGstin = stateCodeFromGstin(party.gstin);
    if (fromGstin) return fromGstin;
  }
  return stateCodeFromName(party.state);
}

/**
 * True when the supply crosses a state line, false when it does not, and
 * **null when it cannot be determined** — no counterparty, no usable state, or
 * the store has no home state configured.
 *
 * Null is a real answer, not an error. The caller must decide what to do with an
 * unknown (keep the operator's choice, warn, or refuse); silently defaulting to
 * intra-state is how the wrong tax head gets charged in the first place.
 */
export function resolveInterState(
  party: CounterpartyLocation | null | undefined,
  homeStateCode: string | null | undefined,
): boolean | null {
  if (!homeStateCode) return null;
  const theirs = counterpartyStateCode(party);
  if (!theirs) return null;
  return theirs !== homeStateCode;
}
